import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Container, Paper, Grid,
  TextField, IconButton, Checkbox, Card, CardMedia,
  CircularProgress, Chip, Divider, Stack, 
  Tooltip, Zoom, Badge, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions,
  useMediaQuery, useTheme
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  CloudUpload as CloudUploadIcon, 
  Add as AddIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Policy as PolicyIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Description as DescriptionIcon,
  Nature as NatureIcon,
  Language as LanguageIcon,
  FactCheck as FactCheckIcon, 
  FolderShared as FolderSharedIcon,
  Warning as WarningIcon,
  DeleteForever as DeleteForeverIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon
} from '@mui/icons-material';
import { useUser } from '../../Context/UserContext'; 
import { GetApi, PostApi } from "../../Services/api/ApiCall"; 
import axios from 'axios';
import API_URLS from '../../Services/api/action';

const sweetGreen = '#4caf50';
const sweetGreenDark = '#2e7d32';
const blackText = '#000000';

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: sweetGreen,
      borderWidth: 1.5,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: sweetGreen,
      borderWidth: 2,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: sweetGreenDark,
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    color: blackText,
    fontWeight: 500,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: blackText,
  },
};

const SectionHeader = ({ icon, title, subtitle, action }) => (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: { xs: 'flex-start', sm: 'center' },
    mb: 3, pb: 2,
    borderBottom: '1px solid', borderColor: 'divider',
    flexDirection: { xs: 'column', sm: 'row' },
    gap: { xs: 1, sm: 0 }
  }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{
        p: 1, borderRadius: 2, bgcolor: sweetGreenDark, color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight="700" sx={{ color: blackText, lineHeight: 1.2, fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
          {title}
        </Typography>
        {subtitle && <Typography variant="body2" sx={{ mt: 0.5, color: blackText }}>{subtitle}</Typography>}
      </Box>
    </Box>
    {action && <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>{action}</Box>}
  </Box>
);

const ArrayInput = ({ icon, label, placeholder, list, setList, temp, setTemp }) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
      {icon}
      <Typography variant="caption" fontWeight="600" sx={{ color: blackText, textTransform: 'uppercase' }}>{label}</Typography>
    </Box>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <TextField 
        size="small" fullWidth placeholder={placeholder} value={temp} 
        onChange={(e) => setTemp(e.target.value)} 
        onKeyPress={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); if (temp.trim()) { setList(prev => [...prev, temp.trim()]); setTemp(''); } }
        }}
        sx={{ ...textFieldSx, '& .MuiOutlinedInput-root': { ...textFieldSx['& .MuiOutlinedInput-root'], borderRadius: 2 } }}
      />
      <Button 
        variant="contained" 
        sx={{ minWidth: { xs: '100%', sm: 80 }, borderRadius: 2, px: 2, py: { xs: 1, sm: 0 }, bgcolor: sweetGreenDark, '&:hover': { bgcolor: '#1b5e20' } }}
        onClick={() => { if (temp.trim()) { setList(prev => [...prev, temp.trim()]); setTemp(''); } }}
      >
        <AddIcon sx={{ mr: 0.5 }} fontSize="small" /> Add
      </Button>
    </Stack>
    {list.length > 0 && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
        {list.map((item, index) => (
          <Zoom key={index} in>
            <Chip label={item} onDelete={() => setList(prev => prev.filter((_, i) => i !== index))} variant="outlined" size="small"
              sx={{ fontWeight: 500, borderColor: sweetGreenDark, color: sweetGreenDark, '& .MuiChip-deleteIcon': { color: sweetGreenDark } }} />
          </Zoom>
        ))}
      </Box>
    )}
  </Box>
);

const LanguageSection = ({ 
  language, aboutValue, setAboutValue, 
  speciesList, setSpeciesList, tempSpecies, setTempSpecies,
  eligibilityList, setEligibilityList, tempEligibility, setTempEligibility,
  documentsList, setDocumentsList, tempDocuments, setTempDocuments,
  aboutLabel, speciesLabel, speciesPlaceholder, 
  eligibilityLabel, eligibilityPlaceholder, documentsLabel, documentsPlaceholder
}) => (
  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <LanguageIcon sx={{ color: blackText, fontSize: 20 }} />
      <Typography variant="subtitle1" fontWeight="600" sx={{ color: blackText, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>{language} Content</Typography>
    </Box>
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <DescriptionIcon sx={{ fontSize: 16, color: blackText }} />
          <Typography variant="caption" fontWeight="600" sx={{ color: blackText, textTransform: 'uppercase' }}>{aboutLabel}</Typography>
        </Box>
        <TextField fullWidth multiline rows={{ xs: 3, sm: 4 }} value={aboutValue} onChange={(e) => setAboutValue(e.target.value)} 
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, height: '100%', alignItems: 'flex-start', '& .MuiOutlinedInput-notchedOutline': { borderColor: sweetGreen, borderWidth: 1.5 }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: sweetGreen, borderWidth: 2 }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: sweetGreenDark, borderWidth: 2 } } }} />
      </Box>
      <ArrayInput icon={<NatureIcon sx={{ fontSize: 16, color: blackText }} />} label={speciesLabel} placeholder={speciesPlaceholder} list={speciesList} setList={setSpeciesList} temp={tempSpecies} setTemp={setTempSpecies} />
      <ArrayInput icon={<FactCheckIcon sx={{ fontSize: 16, color: blackText }} />} label={eligibilityLabel} placeholder={eligibilityPlaceholder} list={eligibilityList} setList={setEligibilityList} temp={tempEligibility} setTemp={setTempEligibility} />
      <ArrayInput icon={<FolderSharedIcon sx={{ fontSize: 16, color: blackText }} />} label={documentsLabel} placeholder={documentsPlaceholder} list={documentsList} setList={setDocumentsList} temp={tempDocuments} setTemp={setTempDocuments} />
    </Box>
  </Box>
);


// ===================== 1. CAROUSEL IMAGE DELETE DIALOG (WITH IMAGE PREVIEWS) =====================
const CarouselDeleteDialog = ({ 
  open, onClose, onConfirm, selectedImages, loading = false 
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, overflow: 'hidden' } }}
      TransitionComponent={Zoom}
      transitionDuration={300}
    >
      {/* Red accent bar */}
      <Box sx={{ height: 6, bgcolor: 'error.main', width: '100%' }} />
      
      <DialogTitle sx={{ pb: 1, pt: 3, px: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{
          p: 1, borderRadius: 2, bgcolor: '#ffebee', color: 'error.main',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <DeleteForeverIcon fontSize="medium" />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight="700" sx={{ color: blackText, lineHeight: 1.3 }}>
            Delete Carousel Images
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.3 }}>
            {selectedImages.length} image{selectedImages.length > 1 ? 's' : ''} selected for deletion
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 1 }}>
        <DialogContentText sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.6, mb: 2.5, pl: 5.5 }}>
          Are you sure you want to permanently remove the following image{selectedImages.length > 1 ? 's' : ''} from the dashboard carousel? This action cannot be undone.
        </DialogContentText>

        {/* Image Preview Grid inside Dialog */}
        <Box sx={{
          maxHeight: { xs: 200, sm: 280 },
          overflow: 'auto',
          bgcolor: '#fafafa',
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: '#eee',
          p: 1.5
        }}>
          <Grid container spacing={1}>
            {selectedImages.map((img) => (
              <Grid item xs={4} sm={3} key={img.id}>
                <Zoom in>
                  <Box sx={{
                    position: 'relative', borderRadius: 1.5, overflow: 'hidden',
                    border: '2px solid', borderColor: 'error.main',
                    boxShadow: 1
                  }}>
                    <CardMedia
                      component="img"
                      image={img.imageurl}
                      alt={`Delete preview ${img.id}`}
                      sx={{ height: { xs: 80, sm: 100 }, width: '100%', objectFit: 'cover' }}
                    />
                    <Box sx={{
                      position: 'absolute', inset: 0,
                      bgcolor: 'rgba(211, 47, 47, 0.35)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <DeleteForeverIcon sx={{ color: 'white', fontSize: 24, opacity: 0.9 }} />
                    </Box>
                    <Box sx={{
                      position: 'absolute', bottom: 0, left: 0, right: 0,
                      bgcolor: 'rgba(0,0,0,0.7)', py: 0.25, textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ color: 'white', fontSize: '0.6rem', fontWeight: 600 }}>ID: {img.id}</Typography>
                    </Box>
                  </Box>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{
          mt: 2, p: 1.5, borderRadius: 2, bgcolor: '#fff3e0',
          border: '1px solid', borderColor: '#ffe0b2',
          display: 'flex', alignItems: 'center', gap: 1
        }}>
          <WarningIcon sx={{ fontSize: 18, color: '#e65100' }} />
          <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600, lineHeight: 1.4 }}>
            These images will be permanently removed from the live dashboard carousel.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        px: { xs: 2, sm: 3 }, py: 2.5, gap: 1,
        flexDirection: { xs: 'column-reverse', sm: 'row' }
      }}>
        <Button onClick={onClose} disabled={loading} variant="outlined"
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, width: { xs: '100%', sm: 'auto' }, borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' } }}>
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={loading} variant="contained" color="error"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3, width: { xs: '100%', sm: 'auto' }, boxShadow: 2 }}>
          {loading ? 'Deleting...' : `Delete ${selectedImages.length} Image${selectedImages.length > 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// ===================== 2. REMOVE SINGLE PREVIEW IMAGE DIALOG =====================
const RemovePreviewDialog = ({ 
  open, onClose, onConfirm, imageData, loading = false 
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, overflow: 'hidden' } }}
      TransitionComponent={Zoom}
      transitionDuration={200}
    >
      <Box sx={{ height: 6, bgcolor: '#ff9800', width: '100%' }} />
      
      <DialogTitle sx={{ pb: 1, pt: 3, px: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{
          p: 1, borderRadius: 2, bgcolor: '#fff3e0', color: '#e65100',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <RemoveCircleOutlineIcon fontSize="medium" />
        </Box>
        <Typography variant="h6" fontWeight="700" sx={{ color: blackText, lineHeight: 1.3 }}>
          Remove Preview Image
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 1 }}>
        <DialogContentText sx={{ color: 'text.secondary', fontSize: '0.9rem', mb: 2, pl: 5.5 }}>
          Remove this image from the upload queue?
        </DialogContentText>

        {/* Single image preview */}
        {imageData && (
          <Box sx={{
            borderRadius: 2, overflow: 'hidden', border: '2px dashed',
            borderColor: '#ffb74d', bgcolor: '#fff8e1', p: 1.5,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1
          }}>
            <Box sx={{ width: '100%', borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: '#ffe0b2' }}>
              <CardMedia
                component="img"
                image={imageData.previewUrl}
                alt={imageData.name}
                sx={{ height: 140, width: '100%', objectFit: 'cover' }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textAlign: 'center', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
              {imageData.name}
            </Typography>
            <Chip label={`${(imageData.size / 1024).toFixed(1)} KB`} size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.7rem' }} />
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        px: { xs: 2, sm: 3 }, py: 2.5, gap: 1,
        flexDirection: { xs: 'column-reverse', sm: 'row' }
      }}>
        <Button onClick={onClose} disabled={loading} variant="outlined"
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, width: { xs: '100%', sm: 'auto' }, borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' } }}>
          Keep It
        </Button>
        <Button onClick={onConfirm} disabled={loading} variant="contained"
          startIcon={<RemoveCircleOutlineIcon />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3, width: { xs: '100%', sm: 'auto' }, bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' }, boxShadow: 2 }}>
          {loading ? 'Removing...' : 'Remove'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// ===================== 3. DELETE SCHEME CONFIRMATION DIALOG =====================
const DeleteSchemeDialog = ({ 
  open, onClose, onConfirm, scheme, loading = false 
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, overflow: 'hidden' } }}
      TransitionComponent={Zoom}
      transitionDuration={300}
    >
      <Box sx={{ height: 6, bgcolor: 'error.main', width: '100%' }} />
      
      <DialogTitle sx={{ pb: 1, pt: 3, px: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{
          p: 1, borderRadius: 2, bgcolor: '#ffebee', color: 'error.main',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <DeleteForeverIcon fontSize="medium" />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight="700" sx={{ color: blackText, lineHeight: 1.3 }}>
            Delete Government Scheme
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.3 }}>
            Scheme #{scheme?.id}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 1 }}>
        <DialogContentText sx={{ color: 'text.secondary', fontSize: '0.9rem', mb: 2.5, pl: 5.5 }}>
          Are you sure you want to permanently delete this scheme? All associated data including English and Tamil content will be removed. This action cannot be undone.
        </DialogContentText>

        {/* Scheme preview card */}
        {scheme && (
          <Box sx={{
            borderRadius: 2.5, overflow: 'hidden', border: '2px solid',
            borderColor: 'error.main', bgcolor: '#fafafa',
            display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5
          }}>
            <Box sx={{ width: { xs: '100%', sm: 140 }, flexShrink: 0, position: 'relative' }}>
              <CardMedia component="img" image={scheme.image_url} alt={`Scheme ${scheme.id}`} sx={{ height: { xs: 120, sm: '100%' }, width: '100%', objectFit: 'cover' }} />
              <Box sx={{
                position: 'absolute', inset: 0,
                bgcolor: 'rgba(211, 47, 47, 0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <DeleteForeverIcon sx={{ color: 'white', fontSize: 36 }} />
              </Box>
            </Box>
            <Box sx={{ p: 1.5, py: { xs: 1, sm: 1.5 }, display: 'flex', flexDirection: 'column', gap: 0.5, overflow: 'hidden' }}>
              <Box>
                <Typography variant="caption" fontWeight="700" sx={{ color: sweetGreenDark, textTransform: 'uppercase', fontSize: '0.6rem' }}>English</Typography>
                <Typography variant="body2" sx={{ color: blackText, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.5, fontSize: '0.8rem' }}>
                  {scheme.about_scheme_en}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.25 }} />
              <Box>
                <Typography variant="caption" fontWeight="700" sx={{ color: sweetGreenDark, textTransform: 'uppercase', fontSize: '0.6rem' }}>Tamil</Typography>
                <Typography variant="body2" sx={{ color: blackText, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.5, fontSize: '0.8rem' }}>
                  {scheme.about_scheme_ta}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        px: { xs: 2, sm: 3 }, py: 2.5, gap: 1,
        flexDirection: { xs: 'column-reverse', sm: 'row' }
      }}>
        <Button onClick={onClose} disabled={loading} variant="outlined"
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, width: { xs: '100%', sm: 'auto' }, borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' } }}>
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={loading} variant="contained" color="error"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3, width: { xs: '100%', sm: 'auto' }, boxShadow: 2 }}>
          {loading ? 'Deleting...' : 'Delete Scheme'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// ===================== 4. EDIT SCHEME CONFIRMATION DIALOG =====================
const EditSchemeDialog = ({ 
  open, onClose, onConfirm, schemeId, schemeImage, loading = false 
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, overflow: 'hidden' } }}
      TransitionComponent={Zoom}
      transitionDuration={300}
    >
      <Box sx={{ height: 6, bgcolor: sweetGreenDark, width: '100%' }} />
      
      <DialogTitle sx={{ pb: 1, pt: 3, px: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{
          p: 1, borderRadius: 2, bgcolor: '#e8f5e9', color: sweetGreenDark,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <EditIcon fontSize="medium" />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight="700" sx={{ color: blackText, lineHeight: 1.3 }}>Edit Government Scheme</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.3 }}>You are about to edit Scheme #{schemeId}</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 1 }}>
        <Box sx={{ 
          mt: 2, p: 2.5, bgcolor: '#f1f8e9', borderRadius: 2.5,
          border: '1px solid', borderColor: '#c8e6c9',
          display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2,
          alignItems: { xs: 'center', sm: 'flex-start' }
        }}>
          {schemeImage && (
            <Box sx={{ width: { xs: '100%', sm: 120 }, height: { xs: 160, sm: 80 }, borderRadius: 2, overflow: 'hidden', flexShrink: 0, border: '1px solid', borderColor: '#a5d6a7' }}>
              <CardMedia component="img" image={schemeImage} alt={`Scheme ${schemeId}`} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </Box>
          )}
          <Box>
            <Typography variant="subtitle2" fontWeight="700" sx={{ color: sweetGreenDark, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <InfoIcon sx={{ fontSize: 16 }} /> What happens next?
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              The form above will be populated with this scheme's existing data. You can modify any field and save your changes. The page will scroll to the top for easy editing.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2.5, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
        <Button onClick={onClose} disabled={loading} variant="outlined"
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, width: { xs: '100%', sm: 'auto' }, borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' } }}>
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={loading} variant="contained" startIcon={<EditIcon />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3, bgcolor: sweetGreenDark, '&:hover': { bgcolor: '#1b5e20' }, width: { xs: '100%', sm: 'auto' }, boxShadow: 2 }}>
          {loading ? 'Loading...' : 'Continue Editing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// ===================== 5. SUCCESS/ERROR MESSAGE DIALOG =====================
const MessageDialog = ({ open, onClose, type, text }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, overflow: 'hidden' } }}
    TransitionComponent={Zoom}
    transitionDuration={200}
  >
    <Box sx={{ height: 6, bgcolor: type === 'success' ? sweetGreenDark : 'error.main', width: '100%' }} />
    <DialogContent sx={{ py: 4, px: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
      <Zoom in={open}>
        <Box sx={{
          p: 2, borderRadius: '50%',
          bgcolor: type === 'success' ? '#e8f5e9' : '#ffebee',
          color: type === 'success' ? sweetGreenDark : 'error.main',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          {type === 'success' ? <CheckCircleIcon sx={{ fontSize: 48 }} /> : <WarningIcon sx={{ fontSize: 48 }} />}
        </Box>
      </Zoom>
      <Typography variant="h6" fontWeight="700" sx={{ color: blackText }}>{type === 'success' ? 'Success!' : 'Oops!'}</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{text}</Typography>
    </DialogContent>
    <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 0 }}>
      <Button onClick={onClose} variant="contained"
        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 5, bgcolor: type === 'success' ? sweetGreenDark : 'error.main', '&:hover': { bgcolor: type === 'success' ? '#1b5e20' : '#c62828' }, boxShadow: 2 }}>
        OK
      </Button>
    </DialogActions>
  </Dialog>
);


// ===================== MAIN COMPONENT =====================
const SuperControl = () => {
  const { authData } = useUser();
  const token = authData?.token;
  const BASE_URL = `/api`;

  // --- Dialog States ---
  const [dialogState, setDialogState] = useState({
    open: false,
    type: '',
    data: null
  });

  const openDialog = (type, data = null) => {
    setDialogState({ open: true, type, data });
  };

  const closeDialog = () => {
    if (!loading) setDialogState(prev => ({ ...prev, open: false }));
  };

  // --- Carousel States ---
  const [newImages, setNewImages] = useState([]); 
  const [existingImages, setExistingImages] = useState([]); 
  const [selectedIds, setSelectedIds] = useState(new Set()); 
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // --- Scheme States ---
  const [schemeImage, setSchemeImage] = useState(null);
  const [aboutSchemeEn, setAboutSchemeEn] = useState('');
  const [speciesListEn, setSpeciesListEn] = useState([]);
  const [tempSpeciesEn, setTempSpeciesEn] = useState('');
  const [aboutSchemeTa, setAboutSchemeTa] = useState('');
  const [speciesListTa, setSpeciesListTa] = useState([]);
  const [tempSpeciesTa, setTempSpeciesTa] = useState('');
  const [eligibilityListEn, setEligibilityListEn] = useState([]);
  const [tempEligibilityEn, setTempEligibilityEn] = useState('');
  const [eligibilityListTa, setEligibilityListTa] = useState([]);
  const [tempEligibilityTa, setTempEligibilityTa] = useState('');
  const [documentsListEn, setDocumentsListEn] = useState([]);
  const [tempDocumentsEn, setTempDocumentsEn] = useState('');
  const [documentsListTa, setDocumentsListTa] = useState([]);
  const [tempDocumentsTa, setTempDocumentsTa] = useState('');
  const [schemes, setSchemes] = useState([]);
  const [isEditingScheme, setIsEditingScheme] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  const [currentEditImageUrl, setCurrentEditImageUrl] = useState('');

  useEffect(() => {
    fetchImages();
    fetchSchemes();
  }, []);

  const fetchImages = async () => {
    try { const data = await GetApi(API_URLS.DashboardCarousel, token); if (data) setExistingImages(data); }
    catch (error) { console.error("Failed to fetch images", error); }
  };

  const fetchSchemes = async () => {
    try { const data = await GetApi(API_URLS.DashboardSchemes, token); if (data) setSchemes(data); }
    catch (error) { console.error("Failed to fetch schemes", error); }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    openDialog('message');
  };

  const handleFileChange = (e) => {
    if (e.target.files) setNewImages((prev) => [...prev, ...Array.from(e.target.files)]);
  };

  const handleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id); else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  // Trigger remove preview dialog
  const triggerRemovePreviewDialog = (index) => {
    const file = newImages[index];
    openDialog('removePreview', {
      index,
      fileData: {
        name: file.name,
        size: file.size,
        previewUrl: URL.createObjectURL(file)
      }
    });
  };

  const confirmRemovePreview = () => {
    const index = dialogState.data?.index;
    if (index !== undefined) {
      setNewImages(prev => prev.filter((_, i) => i !== index));
    }
    closeDialog();
  };

  const handleUploadImages = async () => {
    if (newImages.length === 0) return showMessage('error', 'Select images to upload.');
    setLoading(true);
    const formData = new FormData();
    newImages.forEach((file) => formData.append('images', file));
    try {
      await axios.post(`${BASE_URL}${API_URLS.UploadDashboardCarousel}`, formData, { headers: { Authorization: `Bearer ${token}` } });
      showMessage('success', `${newImages.length} image${newImages.length > 1 ? 's' : ''} uploaded successfully!`);
      setNewImages([]);
      fetchImages();
    } catch (error) { showMessage('error', 'Upload failed. Please try again.'); }
    finally { setLoading(false); }
  };

  const triggerDeleteCarouselDialog = () => {
    if (selectedIds.size === 0) return;
    const selectedImages = existingImages.filter(img => selectedIds.has(img.id));
    openDialog('deleteCarousel', { selectedImages });
  };

  const confirmDeleteCarouselImages = async () => {
    setLoading(true);
    try {
      await PostApi(API_URLS.DeleteDashboardCarousel,{ ids: Array.from(selectedIds) }, token);
      const count = selectedIds.size;
      closeDialog();
      showMessage('success', `${count} image${count > 1 ? 's' : ''} deleted successfully!`);
      setSelectedIds(new Set());
      fetchImages();
    } catch (error) { closeDialog(); showMessage('error', 'Delete failed. Please try again.'); }
    finally { setLoading(false); }
  };

  const triggerEditSchemeDialog = (scheme) => { openDialog('editScheme', { scheme }); };
  const confirmEditScheme = () => { const scheme = dialogState.data?.scheme; closeDialog(); setTimeout(() => { handleEditScheme(scheme); }, 200); };

  const triggerDeleteSchemeDialog = (scheme) => { openDialog('deleteScheme', { scheme }); };
  const confirmDeleteScheme = async () => {
    const scheme = dialogState.data?.scheme;
    setLoading(true);
    try {
      await axios.delete(`${BASE_URL}${API_URLS.DeleteDashboardSchemes}/${scheme.id}`, { headers: { Authorization: `Bearer ${token}` } });
      closeDialog();
      showMessage('success', `Scheme #${scheme.id} deleted successfully!`);
      fetchSchemes();
    } catch (error) { closeDialog(); showMessage('error', 'Delete failed. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleEditScheme = (scheme) => {
    setIsEditingScheme(true);
    setCurrentEditId(scheme.id);
    setAboutSchemeEn(scheme.about_scheme_en);
    setSpeciesListEn(scheme.preferred_species_en || []);
    setAboutSchemeTa(scheme.about_scheme_ta);
    setSpeciesListTa(scheme.preferred_species_ta || []);
    setEligibilityListEn(scheme.scheme_eligibility_en || []);
    setEligibilityListTa(scheme.scheme_eligibility_ta || []);
    setDocumentsListEn(scheme.scheme_documents_en || []);
    setDocumentsListTa(scheme.scheme_documents_ta || []);
    setCurrentEditImageUrl(scheme.image_url);
    setSchemeImage(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetSchemeForm = () => {
    setIsEditingScheme(false); setCurrentEditId(null); setSchemeImage(null);
    setAboutSchemeEn(''); setSpeciesListEn([]); setTempSpeciesEn('');
    setAboutSchemeTa(''); setSpeciesListTa([]); setTempSpeciesTa('');
    setEligibilityListEn([]); setTempEligibilityEn('');
    setEligibilityListTa([]); setTempEligibilityTa('');
    setDocumentsListEn([]); setTempDocumentsEn('');
    setDocumentsListTa([]); setTempDocumentsTa('');
    setCurrentEditImageUrl('');
  };

  const handleSubmitScheme = async () => {
    if (!aboutSchemeEn || !aboutSchemeTa) return showMessage('error', 'Please enter details in both English and Tamil.');
    if (speciesListEn.length === 0 || speciesListTa.length === 0) return showMessage('error', 'Add at least one species in both languages.');
    if (!isEditingScheme && !schemeImage) return showMessage('error', 'Please upload a scheme image.');
    setLoading(true);
    const formData = new FormData();
    formData.append('about_scheme_en', aboutSchemeEn);
    formData.append('preferred_species_en', JSON.stringify(speciesListEn));
    formData.append('about_scheme_ta', aboutSchemeTa);
    formData.append('preferred_species_ta', JSON.stringify(speciesListTa));
    formData.append('scheme_eligibility_en', JSON.stringify(eligibilityListEn));
    formData.append('scheme_eligibility_ta', JSON.stringify(eligibilityListTa));
    formData.append('scheme_documents_en', JSON.stringify(documentsListEn));
    formData.append('scheme_documents_ta', JSON.stringify(documentsListTa));
    if (isEditingScheme) { formData.append('id', currentEditId); formData.append('existing_image', currentEditImageUrl); }
    if (schemeImage) formData.append('scheme_image', schemeImage);
    try {
      if (isEditingScheme) { await axios.put(`${BASE_URL}${API_URLS.UpdateDashboardSchemes}`, formData); showMessage('success', 'Scheme updated successfully!'); }
      else { await axios.post(`${BASE_URL}${API_URLS.UploadDashboardSchemes}`, formData); showMessage('success', 'New scheme added successfully!'); }
      resetSchemeForm(); fetchSchemes();
    } catch (error) { showMessage('error', 'Operation failed. Please try again.'); }
    finally { setLoading(false); }
  };

  const greenButtonSx = {
    bgcolor: sweetGreenDark, '&:hover': { bgcolor: '#1b5e20' },
    borderRadius: 2, px: 4, py: 1.2, textTransform: 'none', fontWeight: 600, boxShadow: 2
  };

  // Get selected images for the delete dialog
  const getSelectedImages = () => existingImages.filter(img => selectedIds.has(img.id));

  return (
    <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 3 } }}>
      
      {/* ===================== ALL DIALOGS ===================== */}
      
      <CarouselDeleteDialog
        open={dialogState.open && dialogState.type === 'deleteCarousel'}
        onClose={closeDialog}
        onConfirm={confirmDeleteCarouselImages}
        selectedImages={getSelectedImages()}
        loading={loading}
      />

      <RemovePreviewDialog
        open={dialogState.open && dialogState.type === 'removePreview'}
        onClose={closeDialog}
        onConfirm={confirmRemovePreview}
        imageData={dialogState.data?.fileData}
        loading={loading}
      />

      <DeleteSchemeDialog
        open={dialogState.open && dialogState.type === 'deleteScheme'}
        onClose={closeDialog}
        onConfirm={confirmDeleteScheme}
        scheme={dialogState.data?.scheme}
        loading={loading}
      />

      <EditSchemeDialog
        open={dialogState.open && dialogState.type === 'editScheme'}
        onClose={closeDialog}
        onConfirm={confirmEditScheme}
        schemeId={dialogState.data?.scheme?.id}
        schemeImage={dialogState.data?.scheme?.image_url}
        loading={loading}
      />

      <MessageDialog
        open={dialogState.open && dialogState.type === 'message'}
        onClose={closeDialog}
        type={message.type}
        text={message.text}
      />


      {/* ================= SECTION 1: CAROUSEL MANAGEMENT ================= */}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, mb: 5, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <SectionHeader
          icon={<PhotoLibraryIcon />}
          title="Carousel Banner Management"
          subtitle="Upload, preview and manage dashboard carousel images"
          action={
            selectedIds.size > 0 && (
              <Badge badgeContent={selectedIds.size} color="error">
                <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={triggerDeleteCarouselDialog} disabled={loading} size="small"
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, width: { xs: '100%', sm: 'auto' } }}>
                  Delete Selected
                </Button>
              </Badge>
            )
          }
        />

        <Box sx={{ border: `2px dashed ${sweetGreen}`, borderRadius: 3, p: { xs: 2, md: 4 }, mb: 4, bgcolor: '#f1f8e9', textAlign: 'center', transition: 'all 0.3s ease', '&:hover': { borderColor: sweetGreenDark, bgcolor: '#e8f5e9' } }}>
          <CloudUploadIcon sx={{ fontSize: { xs: 40, md: 56 }, color: sweetGreen, mb: 1 }} />
          <Typography variant="h6" fontWeight="600" sx={{ color: blackText, mb: 0.5, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>Upload New Banner Images</Typography>
          <Typography variant="body2" sx={{ color: blackText, mb: 2.5 }}>Select multiple images to add to the dashboard carousel</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
            <Button variant="outlined" component="label" startIcon={<ImageIcon />}
              sx={{ borderRadius: 2, px: 4, py: 1.2, textTransform: 'none', fontWeight: 600, borderColor: sweetGreenDark, color: sweetGreenDark, '&:hover': { borderColor: '#1b5e20', bgcolor: '#f1f8e9' }, width: { xs: '100%', sm: 'auto' } }}>
              Browse Files
              <input type="file" multiple accept="image/*" hidden onChange={handleFileChange} />
            </Button>
            {newImages.length > 0 && (
              <Button variant="contained" onClick={handleUploadImages} disabled={loading} startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                sx={{ ...greenButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                Upload {newImages.length} Image{newImages.length > 1 ? 's' : ''}
              </Button>
            )}
          </Stack>
        </Box>

        {newImages.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 1, color: blackText, fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
              <InfoIcon sx={{ fontSize: 16, color: blackText }} /> Preview ({newImages.length} image{newImages.length > 1 ? 's' : ''})
            </Typography>
            <Grid container spacing={2}>
              {newImages.map((file, index) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                  <Zoom in>
                    <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', boxShadow: 2, '&:hover .delete-btn': { opacity: 1 } }}>
                      <CardMedia component="img" image={URL.createObjectURL(file)} alt={`Preview ${index + 1}`} sx={{ height: 140, width: '100%', objectFit: 'cover' }} />
                      <IconButton className="delete-btn" onClick={() => triggerRemovePreviewDialog(index)}
                        sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'error.main', color: 'white', opacity: 0.8, transition: 'opacity 0.2s', '&:hover': { bgcolor: 'error.dark', opacity: 1 }, width: 32, height: 32 }} size="small">
                        <CloseIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'linear-gradient(transparent, rgba(0,0,0,0.7))', p: 1 }}>
                        <Typography variant="caption" color="white" sx={{ fontSize: '0.65rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</Typography>
                      </Box>
                    </Box>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Box>
          <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 1, color: blackText, fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
            <PhotoLibraryIcon sx={{ fontSize: 16, color: blackText }} /> Current Carousel Images ({existingImages.length})
          </Typography>
          {existingImages.length > 0 ? (
            <Grid container spacing={2}>
              {existingImages.map((img) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={img.id}>
                  <Box onClick={() => handleSelect(img.id)} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', cursor: 'pointer', border: selectedIds.has(img.id) ? '3px solid' : '3px solid transparent', borderColor: selectedIds.has(img.id) ? 'error.main' : 'transparent', boxShadow: selectedIds.has(img.id) ? 4 : 1, transition: 'all 0.2s ease', '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' } }}>
                    <CardMedia component="img" height="140" image={img.imageurl} alt={`Carousel ${img.id}`} sx={{ objectFit: 'cover' }} />
                    <Checkbox checked={selectedIds.has(img.id)} sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)', p: 0.5, borderRadius: 1.5, '&.Mui-checked': { bgcolor: 'error.main', color: 'white' } }} />
                    {selectedIds.has(img.id) && <Box sx={{ position: 'absolute', top: 6, left: 6, bgcolor: 'error.main', color: 'white', borderRadius: 1, px: 1, py: 0.25, fontSize: '0.65rem', fontWeight: 700 }}>SELECTED</Box>}
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#f1f8e9', borderRadius: 3, border: `1px dashed ${sweetGreen}` }}>
              <ImageIcon sx={{ fontSize: 48, color: sweetGreen, mb: 1 }} />
              <Typography variant="body1" sx={{ color: blackText, fontWeight: 500 }}>No carousel images uploaded yet</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* ================= SECTION 2: GOVERNMENT SCHEMES FORM ================= */}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 5, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        {isEditingScheme ? (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' }, p: 1.5, px: 2, borderRadius: 2, bgcolor: '#e8f5e9', border: '1px solid', borderColor: '#a5d6a7', flexGrow: 1 }}>
              <EditIcon sx={{ color: sweetGreenDark, fontSize: 20 }} />
              <Typography variant="body2" fontWeight="600" sx={{ color: sweetGreenDark }}>Editing Scheme #{currentEditId} — Modify the fields below and save.</Typography>
            </Box>
            <Button size="small" color="inherit" variant="outlined" onClick={resetSchemeForm} startIcon={<CloseIcon />}
              sx={{ borderRadius: 2, textTransform: 'none', minWidth: { xs: '100%', sm: 'fit-content' }, borderColor: 'divider', color: 'text.secondary' }}>
              Cancel Edit
            </Button>
          </Box>
        ) : (
          <Typography variant="h6" fontWeight="700" sx={{ mb: 3, color: blackText, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>Add New Government Scheme</Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 3 }, width: '100%' }}>
          <Box sx={{ width: { xs: '100%', md: '32%' }, flexShrink: 0 }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ImageIcon sx={{ color: blackText, fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight="600" sx={{ color: blackText, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.75rem' }}>Cover Image</Typography>
              </Box>
              <Box sx={{ width: '100%', flex: 1, minHeight: { xs: 200, md: 280 }, border: `2px dashed ${schemeImage ? sweetGreenDark : sweetGreen}`, borderRadius: 3, overflow: 'hidden', position: 'relative', bgcolor: '#f1f8e9', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', transition: 'all 0.3s', '&:hover': { borderColor: sweetGreenDark, bgcolor: '#e8f5e9' } }} component="label">
                {schemeImage ? (
                  <>
                    <CardMedia component="img" image={URL.createObjectURL(schemeImage)} alt="Scheme preview" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'rgba(0,0,0,0.6)', p: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="white" fontWeight="600">Click to change</Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: { xs: 32, md: 48 }, color: sweetGreen, mb: 1 }} />
                    <Typography variant="body2" sx={{ color: blackText, fontWeight: 500 }}>{isEditingScheme && currentEditImageUrl ? 'Change Image' : 'Click to Upload'}</Typography>
                    <Typography variant="caption" sx={{ color: blackText }}>Recommended: 800x600</Typography>
                  </>
                )}
                <input type="file" accept="image/*" hidden onChange={(e) => setSchemeImage(e.target.files[0])} />
              </Box>
              {isEditingScheme && currentEditImageUrl && !schemeImage && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" fontWeight="600" sx={{ color: blackText, display: 'block', mb: 1 }}>Current Image:</Typography>
                  <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${sweetGreen}`, boxShadow: 1 }}>
                    <CardMedia component="img" image={currentEditImageUrl} alt="Current scheme" sx={{ height: 100, objectFit: 'cover' }} />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ width: { xs: '100%', md: '32%' }, flexShrink: 0 }}>
            <LanguageSection language="English" aboutValue={aboutSchemeEn} setAboutValue={setAboutSchemeEn} speciesList={speciesListEn} setSpeciesList={setSpeciesListEn} tempSpecies={tempSpeciesEn} setTempSpecies={setTempSpeciesEn} eligibilityList={eligibilityListEn} setEligibilityList={setEligibilityListEn} tempEligibility={tempEligibilityEn} setTempEligibility={setTempEligibilityEn} documentsList={documentsListEn} setDocumentsList={setDocumentsListEn} tempDocuments={tempDocumentsEn} setTempDocuments={setTempDocumentsEn} aboutLabel="About Scheme" speciesLabel="Preferred Species" speciesPlaceholder="e.g., Tilapia, Catla" eligibilityLabel="Scheme Eligibility" eligibilityPlaceholder="e.g., Above 18 years old" documentsLabel="Required Documents" documentsPlaceholder="e.g., Aadhar Card, PAN Card" />
          </Box>

          <Box sx={{ width: { xs: '100%', md: '32%' }, flexShrink: 0 }}>
            <LanguageSection language="Tamil" aboutValue={aboutSchemeTa} setAboutValue={setAboutSchemeTa} speciesList={speciesListTa} setSpeciesList={setSpeciesListTa} tempSpecies={tempSpeciesTa} setTempSpecies={setTempSpeciesTa} eligibilityList={eligibilityListTa} setEligibilityList={setEligibilityListTa} tempEligibility={tempEligibilityTa} setTempEligibility={setTempEligibilityTa} documentsList={documentsListTa} setDocumentsList={setDocumentsListTa} tempDocuments={tempDocumentsTa} setTempDocuments={setTempDocumentsTa} aboutLabel="திட்டம் பற்றி" speciesLabel="விருப்பப்படும் இனங்கள்" speciesPlaceholder="எ.கா., திலாப்பியா, கட்லா" eligibilityLabel="திட்ட தகுதி" eligibilityPlaceholder="எ.கா., 18 வயதுக்கு மேற்பட்டவர்கள்" documentsLabel="தேவையான ஆவணங்கள்" documentsPlaceholder="எ.கா., ஆதார் அட்டை, PAN அட்டை" />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2, mt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="contained" size="large" onClick={handleSubmitScheme} disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{ ...greenButtonSx, px: 6, fontSize: '1rem', fontWeight: 700, boxShadow: 3, minWidth: { xs: '100%', md: 200 } }}>
            {loading ? 'Processing...' : (isEditingScheme ? 'Update Scheme' : 'Save Scheme')}
          </Button>
        </Box>
      </Paper>

      {/* ================= SECTION 3: ACTIVE GOVERNMENT SCHEMES ================= */}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <SectionHeader icon={<PolicyIcon />} title="Active Government Schemes" subtitle={`${schemes.length} scheme${schemes.length !== 1 ? 's' : ''} currently displayed on the dashboard`} />

        {schemes.length > 0 ? (
          <Grid container spacing={3}>
            {schemes.map((scheme, index) => (
              <Grid item xs={12} md={6} key={scheme.id}>
                <Zoom in style={{ transitionDelay: `${index * 50}ms` }}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3, border: '1px solid', borderColor: 'divider', transition: 'all 0.3s ease', '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' } }}>
                    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                      <CardMedia component="img" height="200" image={scheme.image_url} alt={`Scheme ${scheme.id}`} sx={{ objectFit: 'cover', transition: 'transform 0.5s ease', '&:hover': { transform: 'scale(1.05)' } }} />
                      <Box sx={{ position: 'absolute', top: 12, left: 12, bgcolor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', color: 'white', borderRadius: 2, px: 1.5, py: 0.5, fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5 }}>SCHEME #{scheme.id}</Box>
                    </Box>
                    <Box sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" fontWeight="700" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', color: sweetGreenDark }}>English</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', color: blackText, lineHeight: 1.6 }}>{scheme.about_scheme_en}</Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" fontWeight="700" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', color: sweetGreenDark }}>Tamil</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', color: blackText, lineHeight: 1.6 }}>{scheme.about_scheme_ta}</Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />

                      {(scheme.preferred_species_en?.length > 0 || scheme.preferred_species_ta?.length > 0) && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" fontWeight="700" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', color: blackText, mb: 0.5, display: 'block' }}>Preferred Species</Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {scheme.preferred_species_en?.length > 0 && (
                              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                <Typography variant="caption" fontWeight="600" sx={{ color: blackText, width: '20px', flexShrink: 0 }}>EN:</Typography>
                                {scheme.preferred_species_en.map((item, i) => <Chip key={`en-sp-${i}`} label={item} size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#e8f5e9', color: sweetGreenDark, fontWeight: 500 }} />)}
                              </Stack>
                            )}
                            {scheme.preferred_species_ta?.length > 0 && (
                              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                <Typography variant="caption" fontWeight="600" sx={{ color: blackText, width: '20px', flexShrink: 0 }}>TA:</Typography>
                                {scheme.preferred_species_ta.map((item, i) => <Chip key={`ta-sp-${i}`} label={item} size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#e8f5e9', color: sweetGreenDark, fontWeight: 500 }} />)}
                              </Stack>
                            )}
                          </Box>
                        </Box>
                      )}

                      {(scheme.scheme_eligibility_en?.length > 0 || scheme.scheme_eligibility_ta?.length > 0) && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" fontWeight="700" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', color: blackText, mb: 0.5, display: 'block' }}>Eligibility</Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {scheme.scheme_eligibility_en?.length > 0 && (
                              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                <Typography variant="caption" fontWeight="600" sx={{ color: blackText, width: '20px', flexShrink: 0 }}>EN:</Typography>
                                {scheme.scheme_eligibility_en.map((item, i) => <Chip key={`en-el-${i}`} label={item} size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#fff3e0', color: '#e65100', fontWeight: 500 }} />)}
                              </Stack>
                            )}
                            {scheme.scheme_eligibility_ta?.length > 0 && (
                              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                <Typography variant="caption" fontWeight="600" sx={{ color: blackText, width: '20px', flexShrink: 0 }}>TA:</Typography>
                                {scheme.scheme_eligibility_ta.map((item, i) => <Chip key={`ta-el-${i}`} label={item} size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#fff3e0', color: '#e65100', fontWeight: 500 }} />)}
                              </Stack>
                            )}
                          </Box>
                        </Box>
                      )}

                      {(scheme.scheme_documents_en?.length > 0 || scheme.scheme_documents_ta?.length > 0) && (
                        <Box sx={{ mb: 2, flexGrow: 1 }}>
                          <Typography variant="caption" fontWeight="700" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', color: blackText, mb: 0.5, display: 'block' }}>Required Documents</Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {scheme.scheme_documents_en?.length > 0 && (
                              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                <Typography variant="caption" fontWeight="600" sx={{ color: blackText, width: '20px', flexShrink: 0 }}>EN:</Typography>
                                {scheme.scheme_documents_en.map((item, i) => <Chip key={`en-doc-${i}`} label={item} size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 500 }} />)}
                              </Stack>
                            )}
                            {scheme.scheme_documents_ta?.length > 0 && (
                              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                <Typography variant="caption" fontWeight="600" sx={{ color: blackText, width: '20px', flexShrink: 0 }}>TA:</Typography>
                                {scheme.scheme_documents_ta.map((item, i) => <Chip key={`ta-doc-${i}`} label={item} size="small" sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 500 }} />)}
                              </Stack>
                            )}
                          </Box>
                        </Box>
                      )}

                      <Divider sx={{ mb: 1.5 }} />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                        <Tooltip title="Edit Scheme" arrow>
                          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => triggerEditSchemeDialog(scheme)}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 2, borderColor: sweetGreenDark, color: sweetGreenDark, '&:hover': { borderColor: '#1b5e20', bgcolor: '#f1f8e9' }, width: { xs: '100%', sm: 'auto' } }}>
                            Edit
                          </Button>
                        </Tooltip>
                        <Tooltip title="Delete Scheme" arrow>
                          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => triggerDeleteSchemeDialog(scheme)}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 2, width: { xs: '100%', sm: 'auto' } }}>
                            Delete
                          </Button>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#f1f8e9', borderRadius: 3, border: `1px dashed ${sweetGreen}` }}>
            <PolicyIcon sx={{ fontSize: 56, color: sweetGreen, mb: 1 }} />
            <Typography variant="h6" sx={{ color: blackText, fontWeight: 600 }}>No schemes added yet</Typography>
            <Typography variant="body2" sx={{ color: blackText }}>Use the form above to add your first government scheme</Typography>
          </Box>
        )}
      </Paper>
      
      <Box sx={{ height: 40 }} />
    </Container>
  );
};

export default SuperControl;